import { useEffect, useRef, useState } from "react";
import { API } from "@/lib/api";
import { X, CaretLeft, CaretRight } from "@phosphor-icons/react";

export default function PhotoLightbox({ photos, startIndex = 0, onClose }) {
  const [idx, setIdx] = useState(startIndex);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  useEffect(() => { setIdx(startIndex); }, [startIndex]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % photos.length);
      if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + photos.length) % photos.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [photos.length, onClose]);

  const onTouchStart = (e) => {
    const t = e.changedTouches[0];
    touchStartX.current = t.clientX;
    touchStartY.current = t.clientY;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStartX.current;
    const dy = t.clientY - touchStartY.current;
    touchStartX.current = null;
    touchStartY.current = null;
    // Only treat as swipe if horizontal movement dominates and clears threshold
    if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy)) return;
    if (dx < 0) setIdx((i) => (i + 1) % photos.length);
    else setIdx((i) => (i - 1 + photos.length) % photos.length);
  };

  if (!photos?.length) return null;
  const p = photos[idx];

  return (
    <div
      data-testid="photo-lightbox"
      className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4 touch-pan-y select-none"
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <button data-testid="lightbox-close" onClick={onClose} className="absolute top-5 right-5 text-white/80 hover:text-white p-2 rounded-full bg-white/10 hover:bg-white/20"><X size={22}/></button>
      {photos.length > 1 && (
        <>
          <button data-testid="lightbox-prev" onClick={(e)=>{e.stopPropagation();setIdx((i)=>(i-1+photos.length)%photos.length);}} className="hidden md:flex absolute left-5 text-white/80 hover:text-white p-3 rounded-full bg-white/10 hover:bg-white/20"><CaretLeft size={22}/></button>
          <button data-testid="lightbox-next" onClick={(e)=>{e.stopPropagation();setIdx((i)=>(i+1)%photos.length);}} className="hidden md:flex absolute right-5 text-white/80 hover:text-white p-3 rounded-full bg-white/10 hover:bg-white/20"><CaretRight size={22}/></button>
        </>
      )}
      <div className="max-w-5xl max-h-[85vh]" onClick={(e)=>e.stopPropagation()}>
        <img data-testid="lightbox-image" src={`${API}/files/${p.id}`} alt={p.filename} draggable={false} className="max-w-full max-h-[85vh] rounded-xl shadow-2xl object-contain pointer-events-none"/>
        <div className="text-white/70 text-xs mt-2 text-center">
          {p.filename} · {idx + 1} / {photos.length}
          {photos.length > 1 && <span className="md:hidden ml-2 opacity-70">· swipe →</span>}
        </div>
      </div>
    </div>
  );
}
